import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { flushSync } from 'react-dom';

import type { TimeZoneMode } from '@/domain/time';

export type ThemeChoice = 'system' | 'light' | 'dark';

export interface Preferences {
  theme: ThemeChoice;
  resolvedTheme: 'light' | 'dark';
  setTheme: (theme: ThemeChoice) => void;
  timeZone: TimeZoneMode;
  setTimeZone: (mode: TimeZoneMode) => void;
  /** The approver's name, stored as entered until authentication exists (TDD §5.6). */
  approver: string;
  setApprover: (name: string) => void;
}

const KEYS = { theme: 'console.theme', timeZone: 'console.timezone', approver: 'console.approver' };

const PreferencesContext = createContext<Preferences | null>(null);

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    /* storage unavailable: the choice lasts for this page only */
  }
}

function systemTheme(): 'light' | 'dark' {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(theme: ThemeChoice): void {
  const root = document.documentElement;
  if (theme === 'system') delete root.dataset.theme;
  else root.dataset.theme = theme;
}

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeChoice>(() => {
    const saved = read(KEYS.theme);
    return saved === 'light' || saved === 'dark' ? saved : 'system';
  });
  const [system, setSystem] = useState<'light' | 'dark'>(() => systemTheme());
  const [timeZone, setTimeZoneState] = useState<TimeZoneMode>(() =>
    read(KEYS.timeZone) === 'local' ? 'local' : 'utc',
  );
  const [approver, setApproverState] = useState<string>(() => read(KEYS.approver) ?? '');

  useEffect(() => {
    const media = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!media) return;
    const onChange = () => setSystem(media.matches ? 'dark' : 'light');
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  useEffect(() => applyTheme(theme), [theme]);

  /**
   * Switch palette with a whole-page cross-fade where the browser can (View Transitions),
   * and at once where it cannot. The document is marked while the fade runs so per-element
   * colour transitions pause and the new snapshot is taken with every colour settled.
   */
  const setTheme = useCallback((next: ThemeChoice) => {
    write(KEYS.theme, next === 'system' ? null : next);
    const start = document.startViewTransition?.bind(document);
    if (!start) {
      setThemeState(next);
      return;
    }
    const root = document.documentElement;
    root.classList.add('theme-switching');
    const transition = start(() => {
      applyTheme(next);
      flushSync(() => setThemeState(next));
    });
    // The palette is applied whether or not the browser runs the cross-fade (it skips one in
    // a hidden tab, or when another transition starts), so a rejection here is not an error.
    void transition.finished
      .finally(() => root.classList.remove('theme-switching'))
      .catch(() => undefined);
  }, []);

  const setTimeZone = useCallback((next: TimeZoneMode) => {
    setTimeZoneState(next);
    write(KEYS.timeZone, next);
  }, []);

  const setApprover = useCallback((next: string) => {
    const trimmed = next.trim();
    setApproverState(trimmed);
    write(KEYS.approver, trimmed || null);
  }, []);

  const value = useMemo<Preferences>(
    () => ({
      theme,
      resolvedTheme: theme === 'system' ? system : theme,
      setTheme,
      timeZone,
      setTimeZone,
      approver,
      setApprover,
    }),
    [theme, system, setTheme, timeZone, setTimeZone, approver, setApprover],
  );

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences(): Preferences {
  const value = useContext(PreferencesContext);
  if (!value) throw new Error('usePreferences must be used inside <PreferencesProvider>.');
  return value;
}

export function useTimeZone(): TimeZoneMode {
  return usePreferences().timeZone;
}

/** A clock that ticks so relative times ("3 min ago") stay honest without re-fetching. */
export function useNow(intervalMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}
